import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import milestoneService from '../../services/milestoneService';
import projectService from '../../services/projectService';
import squadService from '../../services/squadService';
import './MilestoneForm.css';
import RichTextEditor from '../Common/RichTextEditor';

const MilestoneForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [squads, setSquads] = useState([]);
  const [squadsLoading, setSquadsLoading] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    squad_id: '',
    name: '',
    description: '',
    due_date: '',
    completion_date: '',
    status: 'Not Started',
    order_index: 0,
    deliverables: '',
    working_days: '',
    actual_days: '',
    public_holidays: '',
    holiday_note: ''
  });

  useEffect(() => {
    fetchProjects();
    if (isEdit && id) {
      fetchMilestone();
    }
  }, [isEdit, id]);

  // Load squads whenever the selected project changes
  useEffect(() => {
    if (formData.project_id) {
      loadSquads(formData.project_id);
    } else {
      setSquads([]);
    }
  }, [formData.project_id]);

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const loadSquads = async (projectId) => {
    try {
      setSquadsLoading(true);
      const data = await squadService.getSquadsByProject(projectId);
      setSquads(data || []);
    } catch (err) {
      console.error('Failed to fetch squads:', err);
      setSquads([]);
    } finally {
      setSquadsLoading(false);
    }
  };

  const fetchMilestone = async () => {
    try {
      setLoading(true);
      const data = await milestoneService.getMilestoneById(id);
      const milestone = data.data;
      setFormData({
        project_id: milestone.project_id || '',
        squad_id: milestone.squad_id || '',
        name: milestone.name || '',
        description: milestone.description || '',
        due_date: milestone.due_date ? milestone.due_date.split('T')[0] : '',
        completion_date: milestone.completion_date ? milestone.completion_date.split('T')[0] : '',
        status: milestone.status || 'Not Started',
        order_index: milestone.order_index || 0,
        deliverables: milestone.deliverables || '',
        working_days: milestone.working_days || 0,
        public_holidays: milestone.public_holidays || 0,
        holiday_note: milestone.holiday_note || '',
        actual_days: Math.max(
          0,
          (Number(milestone.working_days) || 0) -
          (Number(milestone.public_holidays) || 0)
        ),
      });

      if (milestone.project_id) {
        loadSquads(milestone.project_id);

      }


      setError(null);
    } catch (err) {
      setError('Failed to fetch milestone');
      console.error('Error fetching milestone:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateActualDays = (data) => {
    const workingDays = Number(data.working_days) || 0;
    const publicHolidays = Number(data.public_holidays) || 0;

    return Math.max(0, workingDays - publicHolidays);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "project_id") {
        updated.squad_id = "";
      }

      if (
        name === "working_days" ||
        name === "public_holidays"
      ) {
        updated.actual_days = calculateActualDays(updated);
      }

      return updated;
    });
  };





  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const milestoneData = {
        ...formData,
        order_index: parseInt(formData.order_index) || 0,
        due_date: formData.due_date || null,
        completion_date: formData.completion_date || null
      };

      if (isEdit) {
        await milestoneService.updateMilestone(id, milestoneData);
      } else {
        await milestoneService.createMilestone(milestoneData);
      }

      navigate('/app/milestones');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save milestone');
      console.error('Error saving milestone:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/app/milestones');
  };

  if (loading && isEdit) {
    return <div className="loading">Loading milestone...</div>;
  }

  return (
    <div className="milestone-form-container">
      <div className="milestone-form-header">
        <h2>{isEdit ? 'Edit Milestone' : 'Create New Milestone'}</h2>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit} className="milestone-form">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="form-group">
            <label htmlFor="name">Milestone Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="form-control"
              required
              placeholder="Enter milestone name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="project_id">Project *</label>
            <select
              id="project_id"
              name="project_id"
              value={formData.project_id}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.code})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="squad_id">Squad</label>
            <select
              id="squad_id"
              name="squad_id"
              value={formData.squad_id}
              onChange={handleChange}
              className="form-control"
              disabled={!formData.project_id || squadsLoading}
            >
              <option value="">
                {!formData.project_id
                  ? 'Select a project first'
                  : squadsLoading
                    ? 'Loading squads...'
                    : squads.length === 0
                      ? 'No squads found'
                      : 'Select Squad'}
              </option>
              {squads.map(squad => (
                <option key={squad.id} value={squad.id}>
                  {squad.squad_code} - {squad.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="form-group ">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="form-control"
              required
            >
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div className="form-group ">
            <label htmlFor="due_date">Due Date</label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="form-group custom-form-grp">
            <label htmlFor="completion_date">Completion Date</label>
            <input
              type="date"
              id="completion_date"
              name="completion_date"
              value={formData.completion_date}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        <div className="mb-3">
          <div className="form-group mt-5">
            {/* <label htmlFor="description">Description</label> */}
            <RichTextEditor
              label='Description'
              value={formData.description}
              onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
              placeholder="Enter detailed project description with formatting..."
              height="300px"
            />
          </div>
          <div className="form-group mt-5">
            {/* <label htmlFor="deliverables">Deliverables</label> */}
            <RichTextEditor
              label='Deliverables'
              value={formData.deliverables}
              onChange={(value) => setFormData(prev => ({ ...prev, deliverables: value }))}
              placeholder="Enter detailed deliverables with formatting..."
              height="300px"
            />
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="form-group">
            <label htmlFor="order_index">Order Index</label>
            <input
              type="number"
              id="order_index"
              name="order_index"
              value={formData.order_index}
              onChange={handleChange}
              className="form-control"
              min="0"
              placeholder="0"
            />
            <small className="form-text text-muted">
              Used to order milestones within a project (lower numbers appear first)
            </small>
          </div>
          <div className="form-group">
            <label htmlFor="working_days">Sprint Days</label>
            <input
              type="number"
              id="working_days"
              name="working_days"
              value={formData.working_days}
              onChange={handleChange}
              className="form-control"
              min="0"
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="public_holidays">Public Holidays</label>
            <input
              type="number"
              id="public_holidays"
              name="public_holidays"
              value={formData.public_holidays}
              onChange={handleChange}
              className="form-control"
              min="0"
              placeholder="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="actual_days">Actual Days</label>
            <input
              type="number"
              id="actual_days"
              name="actual_days"
              value={formData.actual_days}
              onChange={handleChange}
              className="form-control"
              min="0"
              placeholder="0"
              readOnly
            />
          </div>

          <div className="form-group w-[25rem]">
            <label htmlFor="holiday_note">Holiday Notes</label>
            <textarea
              type="text"
              id="holiday_note"
              name="holiday_note"
              value={formData.holiday_note}
              onChange={handleChange}
              className="form-control"

              placeholder="Comments...."
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Milestone' : 'Create Milestone')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MilestoneForm;


